import React, { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';

import { gray40, gray80, gray100, red20, red50 } from '../../../constants/colors';
import { regularSm, regularXs } from '../../../constants/location';
import { filterMatchMode, searchMatchMode } from '../../../constants/enum';
import { MasterMappingType } from '../../../types/master';
import { resolveFilterKeys, resolveSearchKey } from '../../../utils/master/matchKeys';
import { ItemDependency } from '../../../utils/master/resolveStepDependencies';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  text: {
    ...regularSm,
    color: gray80,
  },
  // 「絞り込み」「照合項目」などの見出しラベル。値と区別できるよう太字にする（#3180）
  label: {
    fontWeight: 700,
  },
  // 照合方式（[完全一致] など）。行頭に置いて条件の種類を先に読ませる（#3180）
  matchMode: {
    fontWeight: 700,
    marginRight: '4px',
    whiteSpace: 'nowrap',
  },
  // 対応関係の記号（＝ / →）。値に埋もれないよう太字＋色を濃くする（#3180）
  relationMark: {
    color: gray100,
    fontWeight: 700,
    padding: '0 2px',
  },
  indentedList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    // 階層の段差は 16px で揃える（#3308）
    paddingLeft: '16px',
  },
  // 依存注記（#3180）。前段の照合結果を使っていることが目に留まるよう、
  // 背景色付きのチップにして本文から浮かせる。
  // 通常時はグレー（設定として正常なので主張させすぎない）、順序が逆なら赤で警告する。
  dependencyNote: {
    ...regularXs,
    backgroundColor: gray40,
    borderRadius: '4px',
    color: gray100,
    display: 'inline-block',
    fontWeight: 700,
    marginRight: '4px',
    padding: '1px 6px',
    whiteSpace: 'nowrap',
  },
  dependencyWarning: {
    ...regularXs,
    backgroundColor: red20,
    borderRadius: '4px',
    color: red50,
    display: 'inline-block',
    fontWeight: 700,
    marginRight: '4px',
    padding: '1px 6px',
  },
};

interface Props {
  mapping: MasterMappingType;
  /** 先頭にパターン名を表示するか（マスタ一覧タブでは表示、「パターンごとの照合設定」タブでは不要） */
  showPatternName?: boolean;
  /**
   * 入力項目ごとの依存元（#3180）。「パターンごとの照合設定」タブでのみ渡す。
   * 渡された項目には「①の取得項目」と各行に注記する。
   */
  dependencyByItemId?: Map<string, ItemDependency>;
}

/**
 * マッピング1件の設定内容の表示（#3180）。
 * マスタ一覧タブの展開行と、「パターンごとの照合設定」タブのステップ行で共用する。
 *
 * 絞り込みキーが設定されている場合のみ「絞り込み」セクションを出す。
 * 絞り込みなし（既存データを含む）のときは従来と同じ表示になる。
 */
const MappingDetail = ({
  mapping,
  showPatternName = false,
  dependencyByItemId,
}: Props): ReactElement => {
  // 既存データ（search_key / filter_keys 未設定）は旧フィールドから読み替える。
  const searchKey = resolveSearchKey(mapping);
  const filterKeys = resolveFilterKeys(mapping);
  const columnMappings = mapping.column_mappings ?? [];
  const scopeLabel = mapping.scope_item_label || mapping.scope_item_id;

  /**
   * 依存注記のチップ。「どのステップの取得項目を使っているか」だけを示す。
   * 順序が逆の場合は赤くするが、理由の説明はステップ下部のアラートに集約する
   * （チップ内に長文を入れると行が読みにくくなるため）。
   */
  const renderDependencyNote = (itemId: string): ReactElement | null => {
    const dependency = dependencyByItemId?.get(itemId);
    if (!dependency) return null;

    // 実行順の番号は四角バッジと同じ素の数字で表記する（#3308）。
    // 複数あるとき「12」と繋がって読めてしまうため、読点で区切る。
    const format = (orders: number[]): string => orders.join('、');
    const isViolated = dependency.violatedOrders.length > 0;
    const orders = isViolated ? dependency.violatedOrders : dependency.satisfiedOrders;

    return (
      <Typography
        component="span"
        sx={isViolated ? styles.dependencyWarning : styles.dependencyNote}
      >
        {format(orders)}の取得項目
      </Typography>
    );
  };

  return (
    <Box sx={styles.container}>
      {showPatternName && (
        <Typography sx={{ ...regularSm, fontWeight: 700 }}>
          {mapping.pattern_name || '未設定パターン'}
        </Typography>
      )}

      {filterKeys.length > 0 && (
        <>
          <Typography sx={styles.text}>
            <Box component="span" sx={styles.label}>
              絞り込み
            </Box>
            （すべて一致）
          </Typography>
          {/* 「[方式] 照合元 ＝ 照合先」の順に読ませる（#3180）。
              照合は「一致するか」なので ＝、取得項目は「値を入れる」ので → と使い分け、
              項目ラベル内の親子区切り（>）と矢印が混同しないようにする。 */}
          <Box sx={styles.indentedList}>
            {filterKeys.map((filterKey, index) => (
              <Typography key={index} sx={styles.text}>
                <Box component="span" sx={styles.matchMode}>
                  [
                  {filterKey.filter_match_mode === filterMatchMode.keyword
                    ? '部分一致'
                    : '完全一致'}
                  ]
                </Box>
                {renderDependencyNote(filterKey.item_id)}
                {filterKey.item_label || filterKey.item_id}
                <Box component="span" sx={styles.relationMark}>
                  ＝
                </Box>
                {filterKey.csv_column}
              </Typography>
            ))}
          </Box>
        </>
      )}

      <Typography sx={styles.text}>
        <Box component="span" sx={styles.label}>
          照合項目
        </Box>
        ：
        <Box component="span" sx={styles.matchMode}>
          [{searchKey.match_mode === searchMatchMode.keyword ? '部分一致' : 'AI照合'}]
        </Box>
        {renderDependencyNote(searchKey.item_id)}
        {searchKey.item_label || '-'}
        <Box component="span" sx={styles.relationMark}>
          ＝
        </Box>
        {searchKey.csv_column || '-'}
      </Typography>

      {columnMappings.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {columnMappings.map((columnMapping, index) => (
            <Typography key={index} sx={styles.text}>
              <Box component="span" sx={styles.label}>
                取得項目
              </Box>
              ：{columnMapping.csv_column}
              <Box component="span" sx={styles.relationMark}>
                →
              </Box>
              {columnMapping.item_label || columnMapping.item_id}
            </Typography>
          ))}
        </Box>
      )}

      {scopeLabel && (
        <Typography sx={styles.text}>
          <Box component="span" sx={styles.label}>
            照合履歴と一緒に記録する項目
          </Box>
          ：{scopeLabel}
        </Typography>
      )}
    </Box>
  );
};

export default MappingDetail;
